import { Cancel, ChevronLeft, Edit, ExpandLess, ExpandMore, Publish } from "@mui/icons-material";
import { Badge, Box, Chip, Collapse, Divider, Grid, IconButton, Modal, type SxProps, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DrawerTriggerButton } from "../../../components/DrawerTriggerButton/DrawerTriggerButton";
import { HeaderButton } from "../../../components/headerButton";
import { SelfSchedulingButton } from "../../../components/ImportSelfSchedulingButton/SelfSchedulingButton";
import { DRAWER_WIDTH_IN_PX } from "../../../components/RecommendationsDrawer/utils";
import { StaffingChanges } from "../../../components/StaffingChanges/StaffingChanges";
import { useCensusGrouping } from "../../../components/StaffTable/useCensusGrouping";
import { getShiftDeltaCost } from "../../../components/StaffTable/utils";
import { GlobalLocationOrder } from "../../../components/staffing/Header/GlobalLocationOrder";
import { StaffingSettingsPanel } from "../../../components/staffing/Settings/StaffingSettings";
import { StaffingDateTypeSelector } from "../../../components/staffing/StaffingDateTypeSelector";
import { CensusDisplay } from "../../../components/staffingBlock/CensusDisplay";
import { SystemFlatSwitch } from "../../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { StaffAutoComplete } from "../../../components/UIToolkit/Inputs/StaffAutoComplete";
import { CensusBreakdownTooltipContent } from "../../../components/UIToolkit/Popups/CensusBreakdownTooltipContent";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { ThemedSvg } from "../../../components/UIToolkit/ThemedSvg";
import { useDraftMode } from "../../../contexts/DraftModeContext";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useDrawerTriggerNotification } from "../../../contexts/RecommendationsDrawerContext";
import { useUserSettingsContext } from "../../../contexts/Staffing/UserSettingsContext";
import { useWeekViewContext } from "../../../contexts/WeekView";
import { useDayExpenses } from "../../../hooks/useDayExpenses";
import { useHppdByDimension } from "../../../hooks/useHppdByDimension";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { useRevealWage } from "../../../hooks/useRevealWage";
import { NEUTRAL, SURFACE } from "../../../theme/tokens";
import { trackEvent } from "../../../utils/analytics";
import { censusReplacementSummaryLines } from "../../../utils/censusReplacement";
import { byDimensionsOf } from "../../../utils/censusResolution";
import { ALF_DEMO_AIDE_RATIO, ALF_DEMO_NURSE_RATIO, DEFAULT_TARGET_PPD } from "../../../utils/consts";
import { ChangesSource, type DayStats, DraftChangeType, type Nurse, type NurseInShift, StatsMode } from "../../../utils/models";
import {
	countRequestsWithResponses,
	formatCurrency,
	formatNumber,
	getCensus,
	getCensusSourceLabel,
	getCensusSourceName,
	getChipColor,
	getShiftCensus,
	inverseRolesMap,
} from "../../../utils/utilFunctions";
import { DailyPrintoutButton } from "../DailyPrintoutButton";
import { FileUploadModal } from "../FileUploadModal";
import { useDaySearchNurses } from "../useDaySearchNurses";
import { HppdBreakdownTypography } from "./HppdBreakdownTypography";
import { SnfDemoCareStats } from "./SnfDemoCareStats";
import { getStatsBarGridColumns } from "./statsBarGridColumns";
import { MainContainer, MainStatsBarContainer, TopRightPane } from "./styled";

const HeaderDivider = ({ ...props }) => <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: "40px" }} {...props} />;

const CensusTooltip: FC<{ dayIndex: number; weekIndex: number }> = ({ dayIndex, weekIndex }) => {
	const { hospitalData, chosenDepartment, isHospitalView, isHospitalDemo } = useHospitalData();
	const { settings } = useUserSettingsContext();
	const { t } = useTranslation();

	const dayObj = hospitalData.weeks[weekIndex].weeklyShifts[0].days[dayIndex];
	const override = dayObj.census_override;
	const pccCensus = getShiftCensus(hospitalData.weeks[weekIndex], 0, dayIndex);
	const censusSourceLabel = getCensusSourceLabel(chosenDepartment, isHospitalView || isHospitalDemo);

	const summaryLines = [
		...(override
			? [
					{ label: t("census.tooltip.manual"), value: override.override_value },
					{ label: censusSourceLabel, value: pccCensus },
				]
			: [{ label: t("census.tooltip.census"), value: pccCensus }]),
		...censusReplacementSummaryLines(t, getCensusSourceName(chosenDepartment, isHospitalView || isHospitalDemo), dayObj.census_replacement),
	];

	return (
		<CensusBreakdownTooltipContent
			summaryLines={summaryLines}
			mode={settings.censusByLocation}
			byDimensions={byDimensionsOf(dayObj.census_grouping)}
			totalCensus={getCensus(hospitalData.weeks[weekIndex], dayIndex)}
			locations={chosenDepartment.locations}
			statsMode={chosenDepartment.statsMode}
		/>
	);
};

const adaptiveFontSizeSx = {
	fontSize: {
		xs: "12px",
		xl: "14px",
	},
} as const satisfies SxProps;

type DayScheduleHeaderProps = {
	drawerOpen: boolean;
	date: DateTime;
	weekIndex: number;
	dayIndex: number;
	showActual: boolean;
	setShowActual: (isActual: boolean) => void;
	/** The page opened in a chosen view (returning to a wage breakdown): keep it rather than the day's default. */
	keepInitialView: boolean;
	setWeekIndex: (index: number) => void;
	selectedNurse: Nurse | null;
	setSelectedNurse: (nurse: Nurse | null) => void;
	handleDrawerOpen: () => void;
	nurses: NurseInShift[];
	selectedLocations: string[];
	availableLocations: string[];
};
export const DayScheduleHeader: FC<DayScheduleHeaderProps> = ({
	drawerOpen,
	date,
	weekIndex,
	dayIndex,
	showActual,
	nurses,
	setShowActual,
	keepInitialView,
	selectedNurse,
	setSelectedNurse,
	setWeekIndex,
	handleDrawerOpen,
	selectedLocations,
	availableLocations,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const theme = useTheme();
	const {
		hospitalData,
		chosenDepartment,
		departmentNurseTypes,
		getNurseById,
		timezone,
		getWeekIndexByDate,
		prepareDataForAddNurse,
		addNurseToShift,
		isAlf,
		isAlfDemo,
		isSnfDemo,
		isHospital,
		isHospitalDemo,
		isHospitalView,
		showErrorSnackbar,
	} = useHospitalData();
	const formatPersonName = useFormatPersonName();
	const { settings } = useUserSettingsContext();
	const { weekViewData } = useWeekViewContext();
	const { getDrawerTriggerNotification } = useDrawerTriggerNotification();
	const canViewWage = useRevealWage();
	const { isDraftMode, hasUnsavedChanges, enableDraftMode, publishChanges, discardChanges, isPublishing, draftChanges, originalData } = useDraftMode();
	const { groupNursesByLocation, dailyRequiredRatios } = useCensusGrouping(date);
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const [expanded, setExpanded] = useState<boolean>(false);
	const [searchOpen, setSearchOpen] = useState<boolean>(false);
	const [selectedShift, setSelectedShift] = useState<number>(0);
	const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
	const [isDraftButtonHovered, setIsDraftButtonHovered] = useState<boolean>(false);
	const searchableNurses = useDaySearchNurses({ date, dayIndex, showActual });

	const predictedStats = weekViewData[dayIndex].dayStats;
	const actualStats = weekViewData[dayIndex].dayStatsActual;
	const activeStats: DayStats = showActual ? actualStats : predictedStats;

	const predictedHppd = useHppdByDimension(weekViewData.at(dayIndex)?.shifts || [], predictedStats.census, date);
	const actualHppd = useHppdByDimension(weekViewData.at(dayIndex)?.shiftsActual || [], actualStats.census, date);

	const requestsWithResponsesCount = useMemo(() => countRequestsWithResponses(weekViewData.at(dayIndex)?.requests ?? []), [weekViewData, dayIndex]);
	const notification = getDrawerTriggerNotification(requestsWithResponsesCount);

	const roleRatios = useMemo(() => {
		if (!isAlf || activeStats.census === 0) return [];

		const roleCounts: Record<string, number> = {};

		nurses.forEach((nurse) => {
			const nurseDetails = getNurseById(nurse.nurseId);

			if (nurseDetails?.role) {
				const inverseRole = inverseRolesMap[nurseDetails.role] || nurseDetails.role;
				roleCounts[inverseRole] = (roleCounts[inverseRole] || 0) + 1;
			}
		});
		if (chosenDepartment.role_groups && Object.keys(chosenDepartment.role_groups).length) {
			const groupedCounts: Record<string, number> = {};

			for (const [groupName, groupRoles] of Object.entries(chosenDepartment.role_groups)) {
				let groupCount = 0;
				groupRoles.forEach((role) => {
					if (roleCounts[role]) {
						groupCount += roleCounts[role];
					}
				});
				if (groupCount > 0) {
					groupedCounts[groupName] = groupCount;
				}
			}

			return Object.entries(groupedCounts).map(([groupName, count]) => ({
				role: groupName,
				count,
				ratio: groupName.toLowerCase() === "aid" ? Math.round(activeStats.census / ALF_DEMO_AIDE_RATIO) : Math.round(activeStats.census / ALF_DEMO_NURSE_RATIO),
			}));
		}

		return Object.entries(roleCounts).map(([role, count]) => ({
			role,
			count,
			ratio: Math.round(activeStats.census / count),
		}));
	}, [isAlf, nurses, getNurseById, activeStats.census, chosenDepartment.role_groups]);

	if (isAlf) {
		activeStats.wantedHours = roleRatios.reduce((total, role) => total + role.ratio, 0) * 11.5;
	}

	const calculateRoleGroupRatios = useCallback(
		(shifts: NurseInShift[]) => {
			if (chosenDepartment.statsMode !== StatsMode.Ratio || !chosenDepartment.role_groups) return [];

			const groupedNurses = groupNursesByLocation(shifts);

			// Counts come from the scheduled nurses; required comes from the department-wide day total
			// (dailyRequiredRatios) so it includes unstaffed locations — matching the gap view & weekly.
			const aggregatedRoleGroupCounts: Record<string, number> = {};
			for (const roleGroup of Object.keys(chosenDepartment.role_groups)) aggregatedRoleGroupCounts[roleGroup] = 0;
			for (const group of groupedNurses) {
				for (const [roleGroup, count] of Object.entries(group.roleGroupCounts)) {
					aggregatedRoleGroupCounts[roleGroup] = (aggregatedRoleGroupCounts[roleGroup] || 0) + count;
				}
			}

			return Object.entries(aggregatedRoleGroupCounts).map(([role, count]) => ({
				role,
				count,
				ratio: dailyRequiredRatios.roleGroupRatios[role] || 0,
			}));
		},
		[chosenDepartment, groupNursesByLocation, dailyRequiredRatios],
	);

	const calculateJobGroupRatios = useCallback(
		(shifts: NurseInShift[]) => {
			if (chosenDepartment.statsMode !== StatsMode.Ratio) return [];

			const groupedNurses = groupNursesByLocation(shifts);

			// Counts come from the scheduled nurses; required comes from the department-wide day total
			// (dailyRequiredRatios) so it includes unstaffed locations — matching the gap view & weekly.
			const aggregatedJobCounts: Record<string, number> = {};
			for (const group of groupedNurses) {
				for (const [jobId, count] of Object.entries(group.jobGroupCounts)) {
					aggregatedJobCounts[jobId] = (aggregatedJobCounts[jobId] || 0) + count;
				}
			}

			const aggregatedJobRatios = dailyRequiredRatios.jobGroupRatios;
			// Include any job that appears in either counts or ratios.
			const jobIds = new Set<string>([...Object.keys(aggregatedJobCounts), ...Object.keys(aggregatedJobRatios)]);
			return Array.from(jobIds)
				.map((jobId) => ({
					role: chosenDepartment.jobs?.find((j) => j.id === jobId)?.displayName ?? jobId,
					count: aggregatedJobCounts[jobId] || 0,
					ratio: aggregatedJobRatios[jobId] || 0,
				}))
				.sort((a, b) => a.role.localeCompare(b.role));
		},
		[chosenDepartment, groupNursesByLocation, dailyRequiredRatios],
	);

	const predictedRoleGroupRatios = useMemo(
		() => calculateRoleGroupRatios(weekViewData.at(dayIndex)?.shifts || []),
		[calculateRoleGroupRatios, weekViewData, dayIndex],
	);

	const actualRoleGroupRatios = useMemo(
		() => calculateRoleGroupRatios(weekViewData.at(dayIndex)?.shiftsActual || []),
		[calculateRoleGroupRatios, weekViewData, dayIndex],
	);

	const predictedJobGroupRatios = useMemo(
		() => calculateJobGroupRatios(weekViewData.at(dayIndex)?.shifts || []),
		[calculateJobGroupRatios, weekViewData, dayIndex],
	);

	const actualJobGroupRatios = useMemo(
		() => calculateJobGroupRatios(weekViewData.at(dayIndex)?.shiftsActual || []),
		[calculateJobGroupRatios, weekViewData, dayIndex],
	);

	const allowActual = useMemo<boolean>(() => date < DateTime.now().minus({ day: 1 }), [date]);

	const chipColor = useMemo<{ color: string; backgroundColor: string }>(() => getChipColor(activeStats, chosenDepartment), [activeStats, chosenDepartment]);

	const { data: dayExpenses, error: dayExpensesError } = useDayExpenses(
		chosenDepartment.department_id,
		date.toFormat("yyyy-MM-dd"),
		showActual,
		expanded && canViewWage,
	);
	// In draft mode, errors come from the live recompute (not the cached `wage_breakdown_rows`).
	// Surface them — the PPD silently falls back to predicted otherwise.
	useEffect(() => {
		if (isDraftMode && dayExpensesError) {
			showErrorSnackbar(t("draft.mode.live.stats.failed"), { date: date.toFormat("yyyy-MM-dd") }, dayExpensesError);
		}
	}, [isDraftMode, dayExpensesError, showErrorSnackbar, t, date]);
	const overtimeExpenses = dayExpenses?.overtimeExpenses ?? 0;
	const agencyExpenses = dayExpenses?.agencyExpenses ?? 0;

	const draftPpdOverride = useMemo<number | null>(() => {
		if (!isDraftMode || showActual || dayExpenses?.totalCost === undefined || draftChanges.length === 0) return null;
		const census = predictedStats.census;
		return census > 0 ? Math.round((dayExpenses.totalCost / census) * 100) / 100 : 0;
	}, [isDraftMode, showActual, dayExpenses?.totalCost, predictedStats.census, draftChanges.length]);

	const mainStats = useMemo(
		() => ({
			ppd: draftPpdOverride ?? (showActual ? actualStats.ppd : predictedStats.ppd),
			laborPPD: draftPpdOverride ?? (showActual ? actualStats.laborPPD : predictedStats.laborPPD),
			hppd: showActual ? actualHppd : predictedHppd,
			roleGroupRatios: jobManagementEnabled
				? showActual
					? actualJobGroupRatios
					: predictedJobGroupRatios
				: showActual
					? actualRoleGroupRatios
					: predictedRoleGroupRatios,
		}),
		[
			draftPpdOverride,
			showActual,
			actualStats,
			predictedStats,
			actualRoleGroupRatios,
			predictedRoleGroupRatios,
			actualJobGroupRatios,
			predictedJobGroupRatios,
			jobManagementEnabled,
			actualHppd,
			predictedHppd,
		],
	);

	const secondaryStats = useMemo(
		() => ({
			ppd: showActual ? predictedStats.ppd : actualStats.ppd,
			laborPPD: showActual ? predictedStats.laborPPD : actualStats.laborPPD,
			hppd: showActual ? predictedHppd : actualHppd,
			roleGroupRatios: jobManagementEnabled
				? showActual
					? predictedJobGroupRatios
					: actualJobGroupRatios
				: showActual
					? predictedRoleGroupRatios
					: actualRoleGroupRatios,
			label: showActual ? "Scheduled" : "Actual",
		}),
		[
			showActual,
			actualStats,
			predictedStats,
			predictedRoleGroupRatios,
			actualRoleGroupRatios,
			predictedJobGroupRatios,
			actualJobGroupRatios,
			jobManagementEnabled,
			predictedHppd,
			actualHppd,
		],
	);

	const shiftDeltaCost = useMemo<number>(() => {
		if (!showActual) return 0;
		const scheduledShifts = weekViewData.at(dayIndex)?.shifts ?? [];
		return getShiftDeltaCost({
			staff: nurses,
			scheduledShifts,
			department: chosenDepartment,
			shiftDeltaThresholdHours: chosenDepartment.shift_delta_threshold_hours,
			getNurseWage: (nurse) => nurse.cost ?? 0,
			getNurseById,
		});
	}, [showActual, weekViewData, dayIndex, nurses, chosenDepartment, getNurseById]);

	const isFutureDate = useMemo<boolean>(() => date.startOf("day") > DateTime.now().startOf("day"), [date]);
	const isPastData = useMemo<boolean>(() => date.startOf("day") < DateTime.now().startOf("day"), [date]);

	const showSnfDemoCollapsible = isSnfDemo && isFutureDate;

	const handleExpandClick = useCallback((): void => {
		trackEvent("day_view_stats_collapse", { expanded, date: date.toFormat("yyyy-MM-dd") });
		setExpanded(!expanded);
	}, [expanded, date]);

	const { firstShiftDate, lastShiftDate } = useMemo(() => {
		if (!hospitalData.weeks?.length) {
			return { firstShiftDate: null, lastShiftDate: null };
		}

		const firstDate = hospitalData.weeks[0]?.weeklyShifts?.[0]?.days?.[0]?.date;
		const lastDate = hospitalData.weeks?.at(-1)?.weeklyShifts?.[0]?.days?.[6]?.date;

		return {
			firstShiftDate: firstDate || null,
			lastShiftDate: lastDate || null,
		};
	}, [hospitalData.weeks]);

	const isPrevDisabled = useMemo(() => {
		if (!firstShiftDate) return false;
		return date.startOf("day") <= firstShiftDate.startOf("day");
	}, [date, firstShiftDate]);

	const isNextDisabled = useMemo(() => {
		if (!lastShiftDate) return false;
		return date >= lastShiftDate.startOf("day");
	}, [date, lastShiftDate]);

	const handleDateChange = useCallback(
		(direction: "prev" | "next"): void => {
			if (direction === "prev" && isPrevDisabled) return;
			if (direction === "next" && isNextDisabled) return;

			const newDate = direction === "next" ? date.plus({ day: 1 }) : date.minus({ days: 1 });
			const newWeekIndex = getWeekIndexByDate(newDate);

			if (newWeekIndex !== weekIndex) {
				setWeekIndex(newWeekIndex);
			}

			navigate(`/${chosenDepartment.department_id}/day-schedule/${newDate.toFormat("yyyy-MM-dd")}`);
		},
		[date, getWeekIndexByDate, weekIndex, setWeekIndex, navigate, chosenDepartment, isPrevDisabled, isNextDisabled],
	);

	const handleResetDate = useCallback(() => {
		const today = DateTime.now().setZone(timezone);
		navigate(`/${chosenDepartment.department_id}/day-schedule/${today.toFormat("yyyy-MM-dd")}`);
	}, [navigate, timezone, chosenDepartment]);

	const handleDatePickerSelected = useCallback(
		(selectedDate: DateTime) => {
			const newWeekIndex = getWeekIndexByDate(selectedDate);
			if (newWeekIndex !== weekIndex) {
				setWeekIndex(newWeekIndex);
			}
			navigate(`/${chosenDepartment.department_id}/day-schedule/${selectedDate.toFormat("yyyy-MM-dd")}`);
			trackEvent("daily_date_picker_date_selected", {
				selected_date: selectedDate.toFormat("yyyy-MM-dd"),
				previous_date: date.toFormat("yyyy-MM-dd"),
			});
		},
		[getWeekIndexByDate, weekIndex, setWeekIndex, navigate, chosenDepartment, date],
	);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLSelectElement) {
				return;
			}

			if (event.key === "ArrowLeft") {
				event.preventDefault();
				handleDateChange("prev");
			} else if (event.key === "ArrowRight") {
				event.preventDefault();
				handleDateChange("next");
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [handleDateChange]);

	const handleNurseSelected = useCallback(
		(nurse: Nurse, _: DateTime | null) => {
			const nurseToAdd: NurseInShift | undefined = prepareDataForAddNurse(hospitalData, weekIndex, dayIndex, selectedShift, nurse.id, undefined, showActual);

			if (nurseToAdd) {
				addNurseToShift(nurseToAdd, weekIndex, dayIndex, selectedShift, showActual);
			}
			setSearchOpen(false);
		},
		[weekIndex, dayIndex, selectedShift, addNurseToShift, hospitalData, prepareDataForAddNurse, showActual],
	);

	useEffect(() => {
		trackEvent("day_view_with_daily_type", { dailyViewType: settings.dailyView });
	}, [settings.dailyView]);
	// Past days open on Actual, later ones on Scheduled — applied when that flips, not over a view the page was
	// deliberately opened in (DAT-906: returning to a wage breakdown restores its view). A ref of the value last
	// applied, so a StrictMode re-run of the effect doesn't apply it twice.
	const lastDefaultView = useRef<boolean | undefined>(keepInitialView ? allowActual : undefined);
	useEffect(() => {
		if (lastDefaultView.current === allowActual) return;
		lastDefaultView.current = allowActual;
		setShowActual(allowActual);
	}, [allowActual, setShowActual]);

	const isDraftModeEnabled = useFeatureFlag(FEATURE_FLAGS.DraftModeEnabled);

	const isSnfRatio = chosenDepartment.statsMode === StatsMode.Ratio && !!mainStats.roleGroupRatios.length;
	const isAlfRatio = isAlf && !!roleRatios.length;
	const isAnyRatio = isSnfRatio || isAlfRatio;
	const shouldRenderHppdColumn = !isHospital && !settings.hideHppdDailyView;

	return (
		<>
			<MainContainer data-testid="day-schedule-header" drawerOpen={drawerOpen}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<IconButton
						data-testid="day-schedule-back"
						sx={{
							color: theme.palette.primary.main,
							gap: 0.5,
							"&:hover": {
								backgroundColor: alpha(theme.palette.primary.main, 0.04),
							},
						}}
						onClick={() => {
							const path = `/${chosenDepartment.department_id}/staffing`;
							navigate(path);
						}}
					>
						<ChevronLeft />
					</IconButton>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, color: theme.palette.primary.main }}>
						<StaffingDateTypeSelector
							data-annie-target="day-schedule-date"
							initialPageType={"DAILY"}
							currentDateText={date.toFormat("ccc d MMM")}
							onNextClick={() => handleDateChange("next")}
							onPreviousClick={() => handleDateChange("prev")}
							onResetButtonClick={handleResetDate}
							currentDailyDate={date}
							setWeekIndex={setWeekIndex}
							isPrevDisabled={isPrevDisabled}
							isNextDisabled={isNextDisabled}
							currentDate={date}
							onDateSelected={handleDatePickerSelected}
							minDate={firstShiftDate ?? undefined}
							maxDate={lastShiftDate ?? undefined}
						/>
						<StaffAutoComplete
							nurses={searchableNurses.map((nurse) => ({ value: nurse, label: formatPersonName(nurse) }))}
							onChange={(_, value) => setSelectedNurse(value?.value ?? null)}
							value={selectedNurse ? { value: selectedNurse, label: formatPersonName(selectedNurse) } : null}
							dataTestId="day-schedule-staff-search"
							width={210}
						/>
						{allowActual && (
							<SystemFlatSwitch
								data-testid="day-schedule-actual-toggle"
								data-annie-target="day-schedule-actual-toggle"
								options={[
									{
										label: t("scheduled"),
										value: t("scheduled"),
									},
									{
										label: t("actual"),
										value: t("actual"),
									},
								]}
								activeIndexOrValue={showActual ? 1 : 0}
								onSwitch={(_, index) => {
									setShowActual(index === 1);
								}}
							/>
						)}
					</Box>
				</Box>

				{/* Right section */}
				<TopRightPane>
					<SelfSchedulingButton />
					{isDraftModeEnabled &&
						!showActual &&
						!isPastData &&
						(!isDraftMode ? (
							<Box
								component={motion.div}
								onHoverStart={() => setIsDraftButtonHovered(true)}
								onHoverEnd={() => setIsDraftButtonHovered(false)}
								sx={{ display: "inline-block" }}
							>
								<SystemButton
									data-testid="draft-mode-enter-button"
									buttonVariant="secondary"
									size="small"
									tooltip={t("draft.mode")}
									onClick={enableDraftMode}
									sx={{
										border: "none",
										backgroundColor: isDraftButtonHovered ? theme.palette.primary.dark : "transparent",
										"&:hover": {
											backgroundColor: theme.palette.primary.dark,
										},
									}}
									leadingIcon={
										<motion.img
											src={isDraftButtonHovered ? "/draft-mode-white.svg" : "/draft-mode.svg"}
											alt={t("draft.mode")}
											width="20"
											height="20"
											animate={{ y: isDraftButtonHovered ? -2 : 0 }}
											transition={{ duration: 0.2 }}
										/>
									}
								/>
							</Box>
						) : (
							<Box sx={{ display: "flex", gap: 1 }}>
								<SystemButton
									data-testid="draft-mode-discard-button"
									buttonVariant="secondary"
									size="small"
									onClick={discardChanges}
									tooltip={t("draft.mode.discard")}
									disabled={isPublishing}
									leadingIcon={<Cancel fontSize="small" />}
									sx={{ px: drawerOpen ? 1 : 2, py: drawerOpen ? 1 : 0 }}
								>
									{!drawerOpen ? t("draft.mode.discard") : ""}
								</SystemButton>
								<Badge badgeContent={draftChanges.length} color="error" max={99}>
									<SystemButton
										data-testid="draft-mode-publish-button"
										size="small"
										onClick={publishChanges}
										disabled={!hasUnsavedChanges || isPublishing}
										leadingIcon={<Publish fontSize="small" />}
										sx={{ px: drawerOpen ? 1 : 2, py: drawerOpen ? 1 : 0 }}
										tooltip={t("draft.mode.save.changes")}
									>
										{!drawerOpen ? (isPublishing ? t("draft.mode.publishing") : t("draft.mode.save.changes")) : ""}
									</SystemButton>
								</Badge>
							</Box>
						))}
					{!isHospital && (
						<>
							<StaffingSettingsPanel mode="daily" />
							<GlobalLocationOrder />
							<DailyPrintoutButton showActual={showActual} date={date} locations={selectedLocations} availableLocations={availableLocations} />
							<Modal open={showUploadModal} onClose={() => setShowUploadModal(false)} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
								<div>
									<FileUploadModal onClose={() => setShowUploadModal(false)} startDate={date} endDate={date} />
								</div>
							</Modal>
						</>
					)}
					{isHospital && (
						// Same anchor name as the Staffing grid's Post Shift control and as the stats-bar Edit
						// button below: all of them open the Staffing Changes panel, and no two are ever on
						// screen together — `isHospital` splits these two, and React Router mounts this page or
						// the Staffing page, never both.
						<Box data-annie-target="staffing-post-shift" sx={{ display: "flex", alignItems: "center", gap: 1, mx: 1 }}>
							<HeaderButton
								onPress={() => {
									setSearchOpen(false);
									setTimeout(() => setSearchOpen(true), 50);
								}}
								text="Edit"
								state="on"
								disabled={false}
								icon={<Edit />}
							/>
						</Box>
					)}
					<DrawerTriggerButton data-annie-target="staffing-recommendations" open={drawerOpen} notification={notification} onClick={handleDrawerOpen} />
				</TopRightPane>
			</MainContainer>

			{/* Main Stats Bar */}
			{!isHospital && (
				<MainStatsBarContainer data-testid="day-schedule-stats-bar" data-annie-target="day-schedule-stats-bar" drawerOpen={drawerOpen} expanded={expanded}>
					{/* Main Stats Row */}
					<Grid
						container
						data-testid="day-schedule-stats-row"
						sx={{
							py: 1,
							display: "grid",
							gridTemplateColumns: getStatsBarGridColumns({
								shouldRenderPpdColumn: canViewWage,
								shouldRenderHppdColumn: shouldRenderHppdColumn,
								separateHppd: mainStats.hppd.isSeparated,
								isRatio: isAnyRatio,
							}),
							alignItems: "center",
							gap: 0,
						}}
					>
						{/* Percentage Chip */}
						{!isHospital && (
							<Grid>
								<Chip
									data-testid="day-schedule-staffing-percentage"
									label={`${formatNumber((activeStats.totalHours / activeStats.wantedHours) * 100, 1)}%`}
									sx={{
										bgcolor: chipColor.backgroundColor,
										color: chipColor.color,
										fontWeight: 600,
										borderRadius: "16px",
										...adaptiveFontSizeSx,
									}}
								/>
							</Grid>
						)}

						{/* Divider */}
						{!isHospital && canViewWage && (
							<Grid>
								<HeaderDivider />
							</Grid>
						)}

						{/* PPD */}
						{!isHospital && canViewWage && (
							<Grid sx={{ px: 2 }}>
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										gap: 0.5,
									}}
								>
									<Typography sx={{ fontSize: "20px", fontWeight: 600, color: theme.palette.primary.main }}>{isAlf ? "PRD" : "PPD"}</Typography>
									<Box>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 0.5,
												whiteSpace: "nowrap",
											}}
										>
											<Typography
												data-testid="day-schedule-ppd"
												sx={{
													fontWeight: 600,
													color: theme.palette.primary.main,
													...adaptiveFontSizeSx,
												}}
											>
												${mainStats.ppd}
											</Typography>
											{!!chosenDepartment.ppd && chosenDepartment.ppd > DEFAULT_TARGET_PPD && (
												<Typography
													sx={{
														fontWeight: 600,
														color: theme.palette.primary.main,
														...adaptiveFontSizeSx,
													}}
												>
													/ ${chosenDepartment.ppd.toFixed(2)}
												</Typography>
											)}
										</Box>
										<Typography
											sx={{
												fontWeight: 600,
												color: alpha(theme.palette.primary.main, 0.7),
												...adaptiveFontSizeSx,
											}}
										>
											(total labor ${mainStats.laborPPD.toFixed(2)})
										</Typography>
									</Box>
								</Box>
							</Grid>
						)}

						{/* Divider */}
						{!isHospital && (isAnyRatio || shouldRenderHppdColumn) && (
							<Grid>
								<HeaderDivider />
							</Grid>
						)}

						{/* HPPD / Ratio */}
						{!isHospital && (
							<Grid sx={{ px: 2 }}>
								{isSnfRatio ? (
									<Box data-annie-target="day-schedule-role-ratios" sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
										{mainStats.roleGroupRatios.map(({ role, count, ratio }) => (
											<Box key={role} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
												<Tooltip title={role}>
													<Typography
														sx={{
															fontWeight: 600,
															color: theme.palette.primary.main,
															maxWidth: "100px",
															overflow: "hidden",
															textOverflow: "ellipsis",
															textWrap: "nowrap",
															...adaptiveFontSizeSx,
														}}
													>
														{role}
													</Typography>
												</Tooltip>
												<Typography sx={{ fontWeight: 600, color: theme.palette.primary.main, ...adaptiveFontSizeSx }}>
													{count} / {ratio}
												</Typography>
											</Box>
										))}
									</Box>
								) : isAlfRatio ? (
									<Box data-annie-target="day-schedule-role-ratios" sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
										{roleRatios.map(({ role, count, ratio }) => (
											<Box key={role} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
												<Typography sx={{ fontWeight: 600, color: theme.palette.primary.main, ...adaptiveFontSizeSx }}>{role}</Typography>
												<Typography sx={{ fontWeight: 600, color: theme.palette.primary.main, ...adaptiveFontSizeSx }}>
													{count} / {ratio}
												</Typography>
											</Box>
										))}
									</Box>
								) : null}
							</Grid>
						)}

						{shouldRenderHppdColumn && (
							<Grid sx={{ px: 2 }}>
								{/* The walkthrough step says "hours per patient day", so it anchors on the label AND its
								    breakdown together — not on the PPD dollar figure, which is a different measure in a
								    different Grid. Absent when HPPD is hidden, which correctly drops the step. */}
								<Box data-annie-target="day-schedule-hppd" sx={{ display: "flex", gap: 2, alignItems: "center" }}>
									<Typography sx={{ fontSize: "20px", fontWeight: 600, color: theme.palette.primary.main }}>{isAlf ? "Ratio" : "HPPD"}</Typography>
									<HppdBreakdownTypography hppdBreakdownResult={mainStats.hppd} date={date} sxOverride={adaptiveFontSizeSx} />
								</Box>
							</Grid>
						)}

						{/* Divider */}
						<Grid>
							<HeaderDivider />
						</Grid>

						{/* Census */}
						<Grid>
							<Box sx={{ display: "flex", px: 1, gap: 2 }}>
								<CensusDisplay
									source={ChangesSource.DaySchedule}
									dayDate={date}
									onClickMain={() => {
										(isSnfDemo || isAlfDemo || isHospitalView || isHospitalDemo) &&
											navigate(`/${chosenDepartment.department_id}/patients/${date.toFormat("yyyy-MM-dd")}`);
									}}
									censusValue={activeStats.census}
									censusContent={
										<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
											{(() => {
												if (!isDraftMode) {
													return (
														<Typography sx={{ fontSize: "16px", fontWeight: 600, color: theme.palette.primary.main }}>
															{activeStats.census.toFixed(0)}
														</Typography>
													);
												}

												const censusDraftChange = draftChanges.find(
													(change) =>
														(change.type === DraftChangeType.UPDATE_CENSUS || change.type === DraftChangeType.DELETE_CENSUS_OVERRIDE) &&
														change.payload.weekIndex === weekIndex &&
														change.payload.dayIndex === dayIndex,
												);

												if (!censusDraftChange) {
													return (
														<Typography sx={{ fontSize: "16px", fontWeight: 600, color: theme.palette.primary.main }}>
															{activeStats.census.toFixed(0)}
														</Typography>
													);
												}

												const originalCensusOverride =
													originalData?.weeks[weekIndex]?.weeklyShifts[censusDraftChange.payload.shiftIndex]?.days[dayIndex]?.census_override?.override_value;
												const originalCensusFromPCC = originalData
													? getShiftCensus(originalData.weeks[weekIndex], censusDraftChange.payload.shiftIndex, dayIndex)
													: 0;
												const originalCensus = originalCensusOverride ?? originalCensusFromPCC;

												return (
													<>
														<Typography
															sx={{
																fontSize: "12px",
																fontWeight: 400,
																color: theme.palette.primary.main,
																textDecoration: "line-through",
															}}
														>
															{originalCensus?.toFixed(0)}
														</Typography>
														<Typography sx={{ fontSize: "16px", fontWeight: 600, color: theme.palette.primary.main }}>
															{activeStats.census.toFixed(0)}
														</Typography>
													</>
												);
											})()}
										</Box>
									}
									tooltipContent={<CensusTooltip dayIndex={dayIndex} weekIndex={weekIndex} />}
								/>

								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<ThemedSvg src="/staff.svg" alt="staff" size={20} color="var(--inhouse-primary)" />
									<Typography sx={{ fontSize: "16px", fontWeight: 600, color: theme.palette.primary.main }}>{activeStats.totalStaff}</Typography>
								</Box>
							</Box>
						</Grid>
					</Grid>

					{/* Right section with Edit button and expand button */}
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<SystemButton
							data-annie-target="staffing-post-shift"
							data-testid="day-schedule-edit"
							sx={{ height: "fit-content", width: "fit-content", minWidth: "fit-content", px: drawerOpen ? 1 : 2, py: drawerOpen ? 1 : 0 }}
							onClick={() => {
								setSearchOpen(false);
								setTimeout(() => setSearchOpen(true), 50);
							}}
							leadingIcon={<Edit fontSize={"inherit"} />}
						>
							{!drawerOpen ? "Edit" : ""}
						</SystemButton>
						<IconButton data-testid="day-schedule-expand-stats" onClick={handleExpandClick} sx={{ padding: "4px" }}>
							{expanded ? <ExpandLess /> : <ExpandMore />}
						</IconButton>
					</Box>
				</MainStatsBarContainer>
			)}

			{/* Collapsible Extended Stats */}
			{!isHospital && !showSnfDemoCollapsible && (
				<Collapse in={expanded}>
					<Box
						sx={{
							background: SURFACE,
							px: 2,
							py: 1,
							boxShadow: `0px 4px 8px 0px ${alpha(theme.palette.primary.main, 0.15)}`,
							marginRight: drawerOpen ? `${DRAWER_WIDTH_IN_PX}px` : 0,
							transition: "margin-right 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
							borderTop: `1px solid ${NEUTRAL[300]}`,
						}}
					>
						<Grid
							container
							sx={{
								display: "grid",
								gridTemplateColumns: getStatsBarGridColumns({
									shouldRenderPpdColumn: canViewWage,
									shouldRenderHppdColumn: shouldRenderHppdColumn,
									separateHppd: secondaryStats.hppd.isSeparated,
									isRatio: isAnyRatio,
								}),
								alignItems: "center",
								gap: 0,
							}}
						>
							{/* Time Hours Display */}
							{!isHospital && (
								<Grid>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<ThemedSvg src="/time.svg" alt="time" size={16} color="var(--inhouse-primary)" />
										<Typography sx={{ fontSize: "14px", fontWeight: 400, color: alpha(theme.palette.primary.main, 0.7) }}>
											{activeStats.totalHours.toFixed(0)} / {activeStats.wantedHours.toFixed(0)}
										</Typography>
									</Box>
								</Grid>
							)}

							{/* Divider */}
							{!isHospital && canViewWage && (
								<Grid>
									<HeaderDivider />
								</Grid>
							)}

							{!isHospital && canViewWage && (
								<Grid sx={{ px: 2 }}>
									<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 0.5 }}>
										<Typography sx={{ fontSize: "20px", fontWeight: 600, color: alpha(theme.palette.primary.main, 0.7) }}>
											{isAlf ? "PRD" : "PPD"} ({secondaryStats.label})
										</Typography>
										<Box>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 0.5,
													whiteSpace: "nowrap",
												}}
											>
												<Typography
													sx={{
														fontWeight: 600,
														color: alpha(theme.palette.primary.main, 0.7),
														...adaptiveFontSizeSx,
													}}
												>
													${secondaryStats.ppd}
												</Typography>
												{!!chosenDepartment.ppd && chosenDepartment.ppd > DEFAULT_TARGET_PPD && (
													<Typography
														sx={{
															fontWeight: 600,
															color: alpha(theme.palette.primary.main, 0.7),
															...adaptiveFontSizeSx,
														}}
													>
														/ ${chosenDepartment.ppd.toFixed(2)}
													</Typography>
												)}
											</Box>
											<Typography
												sx={{
													fontWeight: 600,
													color: alpha(theme.palette.primary.main, 0.7),
													...adaptiveFontSizeSx,
												}}
											>
												(total labor ${secondaryStats.laborPPD.toFixed(2)})
											</Typography>
										</Box>
									</Box>
								</Grid>
							)}

							{/* Divider */}
							{!isHospital && (isAnyRatio || shouldRenderHppdColumn) && (
								<Grid>
									<HeaderDivider />
								</Grid>
							)}

							{!isHospital && (
								<Grid sx={{ px: 2 }}>
									<Box sx={{ display: "flex", flexDirection: "column" }}>
										{isSnfRatio ? (
											<Box
												sx={{
													display: "flex",
													flexDirection: "column",
													gap: 0.5,
													mt: 0.5,
												}}
											>
												{secondaryStats.roleGroupRatios.map(({ role, count, ratio }) => (
													<Box
														key={role}
														sx={{
															display: "flex",
															justifyContent: "space-between",
															gap: 2,
														}}
													>
														<Tooltip title={role}>
															<Typography
																sx={{
																	fontWeight: 600,
																	color: theme.palette.primary.main,
																	maxWidth: "100px",
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																	textWrap: "nowrap",
																	...adaptiveFontSizeSx,
																	fontSize: 12,
																}}
															>
																{role}
															</Typography>
														</Tooltip>
														<Typography
															sx={{
																fontSize: "12px",
																color: alpha(theme.palette.primary.main, 0.7),
																fontWeight: 600,
															}}
														>
															{count} / {ratio}
														</Typography>
													</Box>
												))}
											</Box>
										) : isAlfRatio ? (
											<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}>
												<Typography sx={{ fontSize: "14px", fontWeight: 600, color: alpha(theme.palette.primary.main, 0.7) }}>
													Ratio ({secondaryStats.label})
												</Typography>
												{roleRatios.map(({ role, count, ratio }) => (
													<Box key={role} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
														<Typography sx={{ fontSize: "12px", color: alpha(theme.palette.primary.main, 0.7), fontWeight: 600 }}>{role}:</Typography>
														<Typography sx={{ fontSize: "12px", color: alpha(theme.palette.primary.main, 0.7), fontWeight: 600 }}>
															{count} / {ratio}
														</Typography>
													</Box>
												))}
											</Box>
										) : null}
									</Box>
								</Grid>
							)}

							{shouldRenderHppdColumn && (
								<Grid sx={{ px: 2 }}>
									<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
										<Typography sx={{ fontSize: "20px", fontWeight: 600, color: theme.palette.primary.main }}>{isAlf ? "Ratio" : "HPPD"}</Typography>
										<HppdBreakdownTypography hppdBreakdownResult={secondaryStats.hppd} date={date} sxOverride={adaptiveFontSizeSx} />
									</Box>
								</Grid>
							)}

							{/* Divider */}
							<Grid>
								<HeaderDivider />
							</Grid>

							{/* Total Money with breakdown */}
							<Grid sx={{ px: 2 }}>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<ThemedSvg src="/money.svg" alt="money" size={20} color="var(--inhouse-primary)" />
									<Typography
										data-testid="day-schedule-total-cost"
										sx={{
											fontSize: "14px",
											color: alpha(theme.palette.primary.main, 0.7),
											fontWeight: 600,
										}}
									>
										{!canViewWage ? "$0" : formatCurrency(activeStats.totalSpent)}
									</Typography>
								</Box>

								{/* Overtime and Agency Breakdown */}
								<Box sx={{ display: "flex", gap: 1 }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Typography
											sx={{
												fontSize: "14px",
												color: alpha(theme.palette.primary.main, 0.7),
												fontWeight: 400,
											}}
										>
											Overtime
										</Typography>
										<Typography
											data-testid="day-schedule-overtime-expenses"
											sx={{
												fontSize: "14px",
												color: alpha(theme.palette.primary.main, 0.7),
												fontWeight: 600,
											}}
										>
											{!canViewWage ? "$0" : formatCurrency(overtimeExpenses)}
										</Typography>
									</Box>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Typography
											sx={{
												fontSize: "14px",
												color: alpha(theme.palette.primary.main, 0.7),
												fontWeight: 400,
											}}
										>
											Agency
										</Typography>
										<Typography
											data-testid="day-schedule-agency-expenses"
											sx={{
												fontSize: "14px",
												color: alpha(theme.palette.primary.main, 0.7),
												fontWeight: 600,
											}}
										>
											{!canViewWage ? "$0" : formatCurrency(agencyExpenses)}
										</Typography>
									</Box>
									{showActual && (
										<Box sx={{ display: "flex", alignItems: "center", gap: 1, whiteSpace: "nowrap" }}>
											<Typography sx={{ fontSize: "14px", color: alpha(theme.palette.primary.main, 0.7), fontWeight: 400, whiteSpace: "nowrap" }}>
												Shift Delta
											</Typography>
											<Typography sx={{ fontSize: "14px", color: alpha(theme.palette.primary.main, 0.7), fontWeight: 600 }}>
												{!canViewWage ? "$0" : formatCurrency(shiftDeltaCost)}
											</Typography>
										</Box>
									)}
								</Box>
							</Grid>
						</Grid>
					</Box>
				</Collapse>
			)}

			{showSnfDemoCollapsible && (
				<SnfDemoCareStats expanded={expanded} drawerOpen={drawerOpen} nurses={nurses} activeStats={activeStats} getNurseById={getNurseById} />
			)}

			{searchOpen && (
				<StaffingChanges
					setIsAdd={() => {
						setSearchOpen(false);
						setTimeout(() => setSearchOpen(false), 100);
					}}
					dayNo={dayIndex}
					shiftNo={selectedShift}
					rolesToDisplay={departmentNurseTypes}
					hideRoomSelect={isHospital}
					onNurseSelected={handleNurseSelected}
					onShiftChange={setSelectedShift}
					onClose={() => {
						setSearchOpen(false);
						setTimeout(() => setSearchOpen(false), 100);
					}}
					showActual={showActual}
				/>
			)}
		</>
	);
};
